import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import AuditForm from "@/components/AuditForm";
import AuditReport from "@/components/AuditReport";
import TerminalLoader from "@/components/TerminalLoader";
import Methodology from "@/components/Methodology";
import RecentAudits from "@/components/RecentAudits";
import PremiumCountdown from "@/components/PremiumCountdown";
import TallyPopup from "@/components/TallyPopup";
import GoogleAnalytics from "@/components/GoogleAnalytics";
import { FileText, ShieldAlert, ExternalLink, Instagram, Mail, MessageCircle, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const reportRef = useRef(null);
  const autorunRef = useRef(false);
  const formSectionRef = useRef(null);

  // Read ?brand=&model=&category=&tier=&autorun=1 query for prefill + optional autorun
  const [prefill, setPrefill] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const brand = params.get("brand");
    const model = params.get("model");
    if (!brand || !model) return;
    const initial = {
      brand,
      model,
      category: params.get("category") || "",
      tier: params.get("tier") === "premium" ? "premium" : "fast",
    };
    setPrefill(initial);
    if (params.get("autorun") === "1" && !autorunRef.current) {
      autorunRef.current = true;
      // Scroll & trigger
      setTimeout(() => {
        formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
      const storedKey = localStorage.getItem("fp_premium_key") || null;
      handleAudit({
        brand: initial.brand,
        model: initial.model,
        category: initial.category || undefined,
        force: true,
        tier: initial.tier,
        premiumKey: initial.tier === "premium" ? storedKey : undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAudit = async (payload) => {
    setLoading(true);
    setError(null);
    setReport(null);
    try {
      const { premiumKey, ...body } = payload || {};
      const headers = premiumKey ? { "X-Premium-Key": premiumKey } : undefined;
      // Always use async job pattern (avoids 60s ingress timeout)
      const startRes = await axios.post(`${API}/audit/start`, body, { timeout: 30000, headers });
      const data = startRes.data || {};

      if (data.status === "complete" && data.report) {
        setReport(data.report);
        setTimeout(() => reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
        return;
      }

      const jobId = data.job_id;
      if (!jobId) throw new Error("Job ID tidak diterima dari server.");

      // Poll job status every 3s up to ~3 minutes
      const maxAttempts = 60;
      let attempt = 0;
      while (attempt < maxAttempts) {
        await new Promise((r) => setTimeout(r, 3000));
        attempt += 1;
        try {
          const poll = await axios.get(`${API}/audit/job/${jobId}`, { timeout: 12000 });
          if (poll.data?.status === "complete" && poll.data.report) {
            setReport(poll.data.report);
            setTimeout(() => reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
            return;
          }
        } catch (pollErr) {
          const status = pollErr?.response?.status;
          if (status === 502 || status === 500) {
            throw new Error(pollErr?.response?.data?.detail || "Audit gagal di background.");
          }
          // 404 or transient — keep polling a few more times
        }
      }
      throw new Error("Timeout — audit terlalu lama. Coba lagi nanti.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Audit gagal.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForceReaudit = () => {
    if (!report) return;
    const storedKey = localStorage.getItem("fp_premium_key") || null;
    handleAudit({
      brand: report.brand,
      model: report.model,
      category: report.category,
      force: true,
      tier: report.model_used && report.model_used.includes("sonnet") ? "premium" : "fast",
      premiumKey: storedKey,
    });
  };

  return (
    <div className="brutal min-h-screen bg-[#fdfdfd] text-[#0a0a0a]" data-testid="landing-page">
      <GoogleAnalytics measurementId="G-L8R7WSYR5P" />
      <TallyPopup formId="gDOeZP" />

      {/* NAV */}
      <header className="border-b-2 border-black bg-[#fdfdfd] sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
            <div className="w-8 h-8 bg-black text-white grid place-items-center font-mono font-bold">FP</div>
            <span className="font-display font-black uppercase tracking-tight text-lg md:text-xl">
              Fakta Produk
            </span>
          </a>
          <nav className="hidden md:flex items-center gap-8 font-mono text-xs uppercase tracking-[0.14em]">
            <a href="#audit" className="hover:underline underline-offset-4" data-testid="nav-audit">Mulai Audit</a>
            <a href="#how" className="hover:underline underline-offset-4" data-testid="nav-faq">FAQ</a>
            <a href="#recent" className="hover:underline underline-offset-4" data-testid="nav-recent">Live Audit</a>
            <a href="https://medium.com/@faktaproduk" target="_blank" rel="noopener noreferrer" className="hover:underline underline-offset-4 inline-flex items-center gap-1" data-testid="nav-blog">
              Blog <ExternalLink className="w-3 h-3" />
            </a>
          </nav>
          <div className="flex items-center gap-3">
            <PremiumCountdown variant="compact" />
            <a
              href="#audit"
              className="bg-black text-white px-4 md:px-5 py-2 md:py-2.5 font-mono font-bold uppercase tracking-[0.14em] text-xs md:text-sm border-2 border-black hover-brutal"
              data-testid="nav-cta"
            >
              Audit Sekarang
            </a>
          </div>
        </div>
      </header>

      {/* HERO + AUDIT FORM (form tetap di hero) */}
      <section id="top" ref={formSectionRef} className="border-b-2 border-black bg-grid">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-12 md:py-16 lg:py-20 grid md:grid-cols-12 gap-8 lg:gap-12">
          {/* Left: Hero copy */}
          <div className="md:col-span-5 lg:col-span-5 md:sticky md:top-24 md:self-start">
            <div className="inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-1.5 font-mono text-xs uppercase tracking-[0.18em]" data-testid="hero-tagline">
              <ShieldAlert className="w-3.5 h-3.5" />
              Chief Auditor untuk Konsumen Indonesia
            </div>
            <h1 className="font-display font-black uppercase tracking-tighter mt-5 text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.9]" data-testid="hero-headline">
              Bedakan
              <br />
              Klaim
              <span className="block text-[#666666]">vs Realita.</span>
            </h1>
            <div className="mt-5 max-w-md text-sm md:text-base leading-relaxed font-sans space-y-3" data-testid="hero-subhead">
              <p>
                Analisis review, keluhan, dan pengalaman pengguna dari berbagai platform <span className="font-bold">sebelum Anda membeli produk</span>.
              </p>
              <p className="font-bold">
                Objektif. Analitis. Tanpa bias influencer.
              </p>
              <p className="font-display font-black uppercase tracking-tight text-lg md:text-xl">
                Cari tahu sebelum menyesal.
              </p>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3 font-mono text-xs md:text-sm uppercase tracking-[0.22em] font-bold">
              <span>INPUT</span>
              <span className="text-[#666]">—</span>
              <span>ANALYZE</span>
              <span className="text-[#666]">—</span>
              <span className="bg-[#FFD700] border-2 border-black px-2 py-0.5">DECIDE</span>
            </div>
          </div>

          {/* Right: Form */}
          <div id="audit" className="md:col-span-7 lg:col-span-7">
            <div className="relative">
              {/* Yellow halo behind form for highlight — larger offset so it's visible */}
              <div
                className="absolute -inset-2 bg-[#FFD700] border-2 border-black translate-x-3 translate-y-3 md:translate-x-4 md:translate-y-4 pointer-events-none"
                style={{ zIndex: 0 }}
                aria-hidden="true"
              />

              {/* Highlight badge "MULAI DI SINI" */}
              <div className="relative flex items-center justify-between mb-4 gap-3 flex-wrap" style={{ zIndex: 1 }}>
                <div className="inline-flex items-center gap-2 border-2 border-black bg-[#0a0a0a] text-[#FFD700] px-3 py-1.5 font-mono text-[10px] md:text-xs uppercase tracking-[0.22em] font-bold">
                  <span className="w-2 h-2 bg-[#FFD700] inline-block animate-pulse" />
                  Mulai di sini · Form Audit
                </div>
                <div className="hidden md:flex font-mono text-[10px] uppercase tracking-[0.18em] border-2 border-black bg-white px-3 py-1.5">
                  ⚡ Gratis · Hasil 60–90 detik
                </div>
              </div>

              {/* Live Premium countdown banner (only renders when premium active) */}
              <div className="relative mb-4" style={{ zIndex: 1 }}>
                <PremiumCountdown variant="banner" />
              </div>

              <div className="relative" style={{ zIndex: 1 }}>
                <AuditForm onSubmit={handleAudit} loading={loading} prefill={prefill} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RESULT */}
      <section ref={reportRef} className="border-b-2 border-black bg-[#fdfdfd]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-16 md:py-24">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#666666]">/ 02 — Output</div>
              <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mt-3 leading-[0.95]">
                Executive
                <br />
                Audit Report.
              </h2>
            </div>
            <div className="font-mono text-xs uppercase tracking-[0.18em] flex items-center gap-2 border-2 border-black bg-white px-3 py-1.5">
              <FileText className="w-3.5 h-3.5" /> Format: Dossier
            </div>
          </div>

          <div className="mt-10">
            {loading && <TerminalLoader />}
            {!loading && error && (
              <div className="border-2 border-black bg-[#FF2400] text-white p-6" data-testid="audit-error">
                <div className="font-mono text-xs uppercase tracking-[0.2em]">[ERROR]</div>
                <div className="font-display font-black text-2xl mt-1">Audit Gagal</div>
                <div className="mt-2 font-mono text-sm">{error}</div>
              </div>
            )}
            {!loading && !error && !report && <EmptyState />}
            {!loading && report && (
              <AuditReport report={report} onForceReaudit={handleForceReaudit} />
            )}
          </div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <Methodology />

      {/* RECENT AUDITS */}
      <RecentAudits />

      {/* FOOTER */}
      <footer className="border-t-2 border-black bg-black text-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-10 grid md:grid-cols-3 gap-8 font-mono text-xs uppercase tracking-[0.18em]">
          <div>
            <div className="font-display font-black text-xl tracking-tight">FAKTA PRODUK</div>
            <div className="mt-2 normal-case tracking-normal text-[#bbbbbb] font-sans">
              Chief Auditor untuk konsumen Indonesia. Berbasis data, bukan opini.
            </div>
            <a
              href="https://www.instagram.com/faktaproduk.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center w-10 h-10 border-2 border-white hover:bg-[#FFD700] hover:text-black hover:border-[#FFD700] transition-colors"
              aria-label="Instagram Fakta Produk"
              title="Instagram · @faktaproduk.ai"
              data-testid="footer-instagram"
            >
              <Instagram className="w-5 h-5" strokeWidth={2} />
            </a>
          </div>

          <div>
            <div className="text-[#bbbbbb]">Kontak</div>
            <ul className="mt-3 space-y-2.5 normal-case tracking-normal font-sans text-sm">
              <li>
                <a
                  href="https://wa.me/628138001862"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 hover:text-[#FFD700] transition-colors"
                  data-testid="footer-whatsapp"
                >
                  <MessageCircle className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
                  <span>+62 813 800 1862</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:support@faktaproduk.com"
                  className="inline-flex items-center gap-2.5 hover:text-[#FFD700] transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
                  <span>support@faktaproduk.com</span>
                </a>
              </li>
              <li>
                <div
                  className="inline-flex items-center gap-2.5 text-[#bbbbbb]"
                  data-testid="footer-location"
                >
                  <MapPin className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
                  <span>DKI Jakarta, Indonesia</span>
                </div>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-[#bbbbbb]">Disclaimer</div>
            <p className="mt-2 normal-case tracking-normal text-[#bbbbbb] font-sans text-sm leading-relaxed">
              Audit dihasilkan oleh PI (Product Intelligence) berdasarkan analisis sinyal publik digital. Seluruh hasil bersifat interpretatif, dinamis, dan digunakan sebagai referensi tambahan sebelum keputusan pembelian.
            </p>
          </div>
        </div>

        <div className="border-t border-[#222] mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-5">
          <div className="font-mono text-[10px] md:text-xs uppercase tracking-[0.18em] text-[#bbbbbb]">
            © {new Date().getFullYear()} — powered by RiTriF Labs. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border-2 border-black bg-white p-10 grid place-items-center text-center" data-testid="empty-state">
      <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#666666]">[ awaiting input ]</div>
      <div className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter mt-3">
        Belum Ada Laporan
      </div>
      <div className="mt-2 max-w-md font-sans text-[#666666]">
        Masukkan merek &amp; model produk pada form di atas untuk menghasilkan
        Executive Audit Report.
      </div>
    </div>
  );
}
